import {
  Box,
  Table,
  Th,
  Thead,
  Tr,
  Flex,
  Text,
  Tbody,
  Td,
  useToast,
} from '@chakra-ui/react';
import { getIn } from 'formik';
import lodash from 'lodash';
import React, { useState, useCallback } from 'react';

import { finleyAssert } from '@finley-technologies/common';
import { typeguards as finleyTypeguards } from '@finley-technologies/finley-types';
import { validate as reportingValidate } from '@finley-technologies/reporting-utilities';

import { EditDataAccessorModal } from './EditDataAccessorModal';
import {
  updateAccessorConfigShell,
  createBorrowingBaseMetricsConfig,
} from './updateAccessorConfigShell';
import {
  AddIconButton,
  DeleteIconButton,
  EditIconButton,
} from 'packages/common-frontend/components/IconButton';
import { Select } from 'packages/common-frontend/components/Select';
import { CreditAgreement } from 'packages/creditAgreements/ui/CreditAgreementFullArtifact/common';
import { CreditAgreementFullArtifactWipConfigType } from 'packages/creditAgreements/ui/types';

const { cloneDeep } = lodash;

type DataAccessorType = CreditAgreement.Config.Reporting.Inputs.DataAccessor.Accessor;

const createDefaultDataAccessor = (idx: number): DataAccessorType => ({
  method: 'borrowingBaseMetrics',
  name: `PLACEHOLDER_${idx}`,
  parameterConfig: createBorrowingBaseMetricsConfig(),
  variables: [],
});

const selectOptions = CreditAgreement.Config.Reporting.Inputs.DataAccessor.Methods.map<{
  value: CreditAgreement.Config.Reporting.Inputs.DataAccessor.MethodType;
  label: CreditAgreement.Config.Reporting.Inputs.DataAccessor.MethodType;
}>((el) => ({ value: el, label: el }));

type DataAccessorViewProps = {
  dataAccessor: DataAccessorType;
  wipConfig?: CreditAgreementFullArtifactWipConfigType;
  basePropertyKey: string;
  fullPropertyKey: string;
  idx: number;
};
const DataAccessorView = ({
  dataAccessor,
  wipConfig,
  basePropertyKey,
  idx,
  fullPropertyKey,
}: DataAccessorViewProps) => {
  const toast = useToast();

  const [isEditModalOpen, setIsEditModalOpen] = useState<boolean>(false);

  const deleteAccessor = (wc: CreditAgreementFullArtifactWipConfigType) => {
    const fields = getIn(wc.formProps.values, basePropertyKey);
    finleyAssert(
      finleyTypeguards.creditAgreement.isReportingInputsDataAccessors(fields),
      `Retrieved non Data Accessors type for ${basePropertyKey}`,
    );

    wc.formProps.setFieldValue(
      basePropertyKey,
      fields.filter((_, varIdx) => varIdx !== idx),
    );
  };

  const onSave = useCallback(
    (newAccessor: DataAccessorType): void => {
      if (!wipConfig) {
        return;
      }

      const dataAccessors = getIn(wipConfig.formProps.values, basePropertyKey);
      finleyAssert(
        finleyTypeguards.creditAgreement.isReportingInputsDataAccessors(
          dataAccessors,
        ),
        `Retrieved non Data Accessors type for ${basePropertyKey}`,
      );

      const adjustedDataAccessors = dataAccessors.map(
        (currentAccessor, dIdx) => {
          if (dIdx === idx) {
            return newAccessor;
          }
          return currentAccessor;
        },
      );

      const dataAccessorErrors = reportingValidate.input.validateDataAccessors({
        dataAccessors: adjustedDataAccessors,
      });
      if (dataAccessorErrors.length > 0) {
        toast({
          title: 'Data Accessor Error',
          description: dataAccessorErrors.join(', '),
          status: 'error',
          position: 'top',
          isClosable: true,
        });
        return;
      }

      wipConfig.formProps.setFieldValue(fullPropertyKey, newAccessor);
      setIsEditModalOpen(false);
    },
    [wipConfig, basePropertyKey, fullPropertyKey, idx, toast],
  );

  return (
    <Tr>
      <Td>{dataAccessor.name}</Td>
      <Td>
        {wipConfig?.isMutateAllowed(fullPropertyKey) ? (
          <Select
            value={dataAccessor.method}
            onChange={(method) => {
              if (!method) {
                return;
              }
              const targetDataAccessor = cloneDeep(
                getIn(wipConfig.formProps.values, fullPropertyKey),
              );
              finleyAssert(
                finleyTypeguards.creditAgreement.isReportingInputsDataAccessor(
                  targetDataAccessor,
                ),
                `Retrieved non Data Accessor type for ${fullPropertyKey}`,
              );

              targetDataAccessor.method = method;
              const updatedAccessor = updateAccessorConfigShell({
                dataAccessor: targetDataAccessor,
              });
              wipConfig.formProps.setFieldValue(
                fullPropertyKey,
                updatedAccessor,
              );
            }}
            options={selectOptions}
            width="xl"
          />
        ) : (
          <Text>{dataAccessor.method}</Text>
        )}
      </Td>

      <Td>
        <Box maxH="48" overflowY="auto" as="pre" fontSize="sm">
          {JSON.stringify(dataAccessor.parameterConfig, null, 2)}
        </Box>
      </Td>
      {wipConfig?.isMutateAllowed(fullPropertyKey) && (
        <Td>
          <EditDataAccessorModal
            isOpen={isEditModalOpen}
            onCancel={() => setIsEditModalOpen(false)}
            onSave={onSave}
            dataAccessor={dataAccessor}
          />
          <EditIconButton
            ariaLabel="edit data accessor"
            label="Edit Data Accessor"
            onClick={() => setIsEditModalOpen(true)}
          />
          <DeleteIconButton
            ariaLabel="delete data accessor"
            label="Delete Data Accessor"
            onClick={() => {
              deleteAccessor(wipConfig);
            }}
          />
        </Td>
      )}
    </Tr>
  );
};

type ReportingDataAccessorsProps = {
  dataAccessors: DataAccessorType[];
  wipConfig?: CreditAgreementFullArtifactWipConfigType;
  basePropertyKey: string;
};
export const ReportingDataAccessors = ({
  wipConfig,
  basePropertyKey,
  dataAccessors,
}: ReportingDataAccessorsProps) => {
  const addAccessor = (wc: CreditAgreementFullArtifactWipConfigType) => {
    const queries = cloneDeep(getIn(wc.formProps.values, basePropertyKey));
    const query = createDefaultDataAccessor(queries.length + 1);
    queries.push(query);
    wc.formProps.setFieldValue(basePropertyKey, queries);
  };

  return (
    <Box mb="6">
      <Flex alignItems="center" mb="3">
        <Text fontWeight="bold" fontSize="large" mr="2">
          Data Accessors
        </Text>

        {wipConfig?.isMutateAllowed(basePropertyKey) && (
          <AddIconButton
            ariaLabel="add data accessor"
            label="Add Data Accessor"
            onClick={() => {
              addAccessor(wipConfig);
            }}
          />
        )}
      </Flex>

      {dataAccessors.length > 0 ? (
        <Table borderWidth="1px">
          <Thead>
            <Tr>
              <Th>Name</Th>
              <Th>Method</Th>
              <Th>Params</Th>
              {wipConfig && <Th>Edit/Delete</Th>}
            </Tr>
          </Thead>
          <Tbody>
            {dataAccessors.map((dataAccessor, idx) => (
              <DataAccessorView
                key={`${basePropertyKey}-${dataAccessor.name}`}
                dataAccessor={dataAccessor}
                basePropertyKey={basePropertyKey}
                fullPropertyKey={`${basePropertyKey}[${idx}]`}
                idx={idx}
                wipConfig={wipConfig}
              />
            ))}
          </Tbody>
        </Table>
      ) : (
        <Text p="3" border="1px" borderColor="gray.700">
          There are currently no data accessors configured.
        </Text>
      )}
    </Box>
  );
};
